// Copyright © 2019-2026 Focal Systems. All rights reserved.

// Works out which calendar days a schedule exception's rule can apply on, so the timeline can
// show that day's opening hours instead of the store's generic ones.
//
// The rules are Python expressions run through `eval` by the camera scheduler
// (backend-legacy `focal/jobs/scheduler/utils.py`, `calculate_schedule_for_camera`), once per
// scheduled day and camera, with these variables in scope:
//
//   day_of_week    str   full English weekday name  — `date.strftime('%A')` → 'Saturday'
//   month          str   full English month name    — `date.strftime('%B')` → 'December'
//   day_of_month   int   1–31
//   tag_names      list[str]   camera's mod tags
//   processing_types list[str] camera's pipelines
//   camera_type    str         camera model
//   has_prefix(s, prefixes) -> bool
//
// Only the three date variables are knowable here; everything else depends on the camera, so it
// evaluates to *unknown* and propagates through three-valued (Kleene) logic. A day counts as a
// candidate when the rule comes out true **or** unknown for it — i.e. the day isn't ruled out.
// Anything the restricted grammar can't read also degrades to unknown rather than failing, which
// widens the candidate set and makes the caller fall back to the store's default hours.

import { tokenize } from "@/lib/expression-validator";

/** Full English weekday names, in `Date.getDay()` order — `%A` emits exactly these. */
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

/** Full English month names, in `Date.getMonth()` order — `%B` emits exactly these. */
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** The date variables a rule can be read against. */
interface DayContext {
  dayOfWeek: string;
  month: string;
  dayOfMonth: number;
}

export function dayContextOf(date: Date): DayContext {
  return {
    dayOfWeek: WEEKDAY_NAMES[date.getDay()] ?? "",
    month: MONTH_NAMES[date.getMonth()] ?? "",
    dayOfMonth: date.getDate(),
  };
}

// Kleene truth: `null` is "depends on something we can't see here" (a camera variable, or a
// construct the reader doesn't support).
type Tri = boolean | null;

function triAnd(a: Tri, b: Tri): Tri {
  if (a === false || b === false) return false;
  if (a === null || b === null) return null;
  return true;
}

function triOr(a: Tri, b: Tri): Tri {
  if (a === true || b === true) return true;
  if (a === null || b === null) return null;
  return false;
}

type Node =
  | { kind: "and"; items: Node[] }
  | { kind: "or"; items: Node[] }
  | { kind: "not"; item: Node }
  | { kind: "test"; test: (ctx: DayContext) => boolean }
  | { kind: "unknown" };

const UNKNOWN: Node = { kind: "unknown" };

function evaluate(node: Node, ctx: DayContext): Tri {
  switch (node.kind) {
    case "and":
      return node.items.reduce<Tri>((acc, item) => triAnd(acc, evaluate(item, ctx)), true);
    case "or":
      return node.items.reduce<Tri>((acc, item) => triOr(acc, evaluate(item, ctx)), false);
    case "not": {
      const inner = evaluate(node.item, ctx);
      return inner === null ? null : !inner;
    }
    case "test":
      return node.test(ctx);
    case "unknown":
      return null;
  }
}

type Tokens = ReturnType<typeof tokenize>;

const OPEN_BRACKETS = new Set(["(", "[", "{"]);
const CLOSE_BRACKETS = new Set([")", "]", "}"]);

class DayRuleParser {
  private pos = 0;

  constructor(private readonly tokens: Tokens) {}

  parse(): Node {
    const node = this.parseOr();
    // Trailing junk means the reader lost the thread — don't trust the part it did read.
    return this.pos < this.tokens.length ? UNKNOWN : node;
  }

  private peek(): Tokens[number] | undefined {
    return this.tokens[this.pos];
  }

  private isKeyword(value: string): boolean {
    const token = this.peek();
    return token?.kind === "name" && token.value === value;
  }

  private parseOr(): Node {
    const items = [this.parseAnd()];
    while (this.isKeyword("or")) {
      this.pos++;
      items.push(this.parseAnd());
    }
    return items.length === 1 ? (items[0] as Node) : { kind: "or", items };
  }

  private parseAnd(): Node {
    const items = [this.parseNot()];
    while (this.isKeyword("and")) {
      this.pos++;
      items.push(this.parseNot());
    }
    return items.length === 1 ? (items[0] as Node) : { kind: "and", items };
  }

  private parseNot(): Node {
    // `not in` is a comparison operator, not a negation — leave it to the atom reader.
    if (this.isKeyword("not") && !(this.tokens[this.pos + 1]?.value === "in")) {
      this.pos++;
      return { kind: "not", item: this.parseNot() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const token = this.peek();
    if (!token) return UNKNOWN;
    if (token.value === "(" && token.kind === "op") {
      // A parenthesised group is only a group if it closes cleanly and holds a boolean expression;
      // `has_prefix(x, ('BK',))` also starts with `(` after a name, but that name was consumed as
      // part of an atom span before we ever get here.
      const save = this.pos;
      this.pos++;
      const inner = this.parseOr();
      if (this.peek()?.value === ")") {
        this.pos++;
        return inner;
      }
      this.pos = save;
      return this.skipAtom();
    }
    return this.readAtom();
  }

  /** Consume one atom's tokens: everything up to a top-level `and` / `or` / closing bracket. */
  private takeAtomSpan(): Tokens {
    const start = this.pos;
    let depth = 0;
    while (this.pos < this.tokens.length) {
      const token = this.tokens[this.pos];
      if (!token) break;
      if (token.kind === "op" && OPEN_BRACKETS.has(token.value)) depth++;
      else if (token.kind === "op" && CLOSE_BRACKETS.has(token.value)) {
        if (depth === 0) break;
        depth--;
      } else if (depth === 0 && token.kind === "name" && (token.value === "and" || token.value === "or")) break;
      this.pos++;
    }
    return this.tokens.slice(start, this.pos);
  }

  private skipAtom(): Node {
    this.takeAtomSpan();
    return UNKNOWN;
  }

  private readAtom(): Node {
    return readComparison(this.takeAtomSpan());
  }
}

const NUMERIC_OPS = new Set(["<", "<=", ">", ">="]);
const COMPARISON_OPS = new Set(["==", "!=", "<", "<=", ">", ">="]);

/** Mirror an operator so `25 == day_of_month` reads the same as `day_of_month == 25`. */
const MIRRORED: Record<string, string> = { "<": ">", "<=": ">=", ">": "<", ">=": "<=", "==": "==", "!=": "!=" };

type Literal = string | number;

/** A string or number literal, or a collection of them (`['a', 'b']`, `(1, 2)`, `{'a'}`). */
function readLiterals(tokens: Tokens): Literal[] | null {
  if (tokens.length === 0) return null;
  const first = tokens[0];
  if (!first) return null;
  if (tokens.length === 1) {
    if (first.kind === "str") return [unquote(first.value)];
    if (first.kind === "num") return [Number(first.value)];
    return null;
  }
  const last = tokens[tokens.length - 1];
  const opens: Record<string, string> = { "[": "]", "(": ")", "{": "}" };
  if (first.kind !== "op" || !opens[first.value] || last?.value !== opens[first.value]) return null;
  const values: Literal[] = [];
  // Items alternate literal, comma, literal … — a trailing comma (Python's 1-tuple) is fine.
  for (let i = 1; i < tokens.length - 1; i++) {
    const token = tokens[i];
    if (!token) return null;
    if (token.kind === "op" && token.value === ",") continue;
    if (token.kind === "str") values.push(unquote(token.value));
    else if (token.kind === "num") values.push(Number(token.value));
    else return null;
  }
  return values;
}

function unquote(raw: string): string {
  return raw.slice(1, -1).replace(/\\(.)/g, "$1");
}

/** Read one atom as a comparison against a date variable, or give up with `unknown`. */
function readComparison(tokens: Tokens): Node {
  // Find the operator at bracket depth 0: `==`/`!=`/`<`… or the `in` / `not in` keywords.
  let depth = 0;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) break;
    if (token.kind === "op" && OPEN_BRACKETS.has(token.value)) depth++;
    else if (token.kind === "op" && CLOSE_BRACKETS.has(token.value)) depth--;
    if (depth !== 0) continue;

    if (token.kind === "op" && COMPARISON_OPS.has(token.value)) {
      return buildComparison(tokens.slice(0, i), token.value, tokens.slice(i + 1));
    }
    if (token.kind === "name" && token.value === "in") {
      return buildMembership(tokens.slice(0, i), tokens.slice(i + 1), false);
    }
    if (token.kind === "name" && token.value === "not" && tokens[i + 1]?.value === "in") {
      return buildMembership(tokens.slice(0, i), tokens.slice(i + 2), true);
    }
  }
  return UNKNOWN;
}

type DateVariable = "day_of_week" | "month" | "day_of_month";

const DATE_VARIABLES = new Set<string>(["day_of_week", "month", "day_of_month"]);

function asVariable(tokens: Tokens): DateVariable | null {
  const only = tokens.length === 1 ? tokens[0] : undefined;
  if (only?.kind !== "name" || !DATE_VARIABLES.has(only.value)) return null;
  return only.value as DateVariable;
}

function contextValue(variable: DateVariable, ctx: DayContext): Literal {
  if (variable === "day_of_week") return ctx.dayOfWeek;
  if (variable === "month") return ctx.month;
  return ctx.dayOfMonth;
}

function buildComparison(left: Tokens, op: string, right: Tokens): Node {
  let variable = asVariable(left);
  let literals = readLiterals(right);
  let effectiveOp = op;
  if (!variable) {
    // Reversed form: `'Saturday' == day_of_week`.
    variable = asVariable(right);
    literals = readLiterals(left);
    effectiveOp = MIRRORED[op] ?? op;
  }
  const literal = literals?.length === 1 ? literals[0] : undefined;
  if (!variable || literal === undefined) return UNKNOWN;
  // Ordering only makes sense on day_of_month; `day_of_week > 'Friday'` compares strings in Python
  // and is more likely a mistake than an intent worth honouring.
  if (NUMERIC_OPS.has(effectiveOp) && (variable !== "day_of_month" || typeof literal !== "number")) return UNKNOWN;

  return {
    kind: "test",
    test: (ctx) => {
      const value = contextValue(variable, ctx);
      switch (effectiveOp) {
        case "==":
          return value === literal;
        case "!=":
          return value !== literal;
        case "<":
          return (value as number) < (literal as number);
        case "<=":
          return (value as number) <= (literal as number);
        case ">":
          return (value as number) > (literal as number);
        case ">=":
          return (value as number) >= (literal as number);
        default:
          return false;
      }
    },
  };
}

function buildMembership(left: Tokens, right: Tokens, negated: boolean): Node {
  const variable = asVariable(left);
  const literals = readLiterals(right);
  // `'BK' in tag_names` has a literal on the left and a camera variable on the right — unknowable.
  if (!variable || !literals) return UNKNOWN;
  return {
    kind: "test",
    test: (ctx) => literals.includes(contextValue(variable, ctx)) !== negated,
  };
}

/** A rule read as "can this exception apply on this date?". */
export type RuleDayPredicate = (date: Date) => boolean;

/** Every day is a candidate — used for an absent rule and for anything unreadable. */
const ALWAYS: RuleDayPredicate = () => true;

/**
 * Compile a rule into a day predicate. The predicate answers *possibility*, not certainty: a day
 * passes unless the rule's date conditions rule it out, so camera-dependent rules
 * (`camera_type == 'sc25'`) keep every day and a malformed rule degrades to the same.
 */
export function compileRuleDays(rule: string | undefined): RuleDayPredicate {
  const trimmed = rule?.trim();
  if (!trimmed) return ALWAYS;
  let node: Node;
  try {
    node = new DayRuleParser(tokenize(trimmed)).parse();
  } catch {
    return ALWAYS;
  }
  if (node.kind === "unknown") return ALWAYS;
  return (date) => evaluate(node, dayContextOf(date)) !== false;
}
